/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button, Grid, breakpoints, typography2 } from '@coursera/cds-core';

import TrackedTextInput from 'bundles/page/components/TrackedTextInput';

import _t from 'i18n!nls/discussions';

type Props = {
  handleSubmit: () => void;
  handleChange: (x: string) => void;
  query: string;
  handleBlur: () => void;
  handleTab: (event: React.KeyboardEvent<HTMLElement>) => void;
  handleShiftTab: (event: React.KeyboardEvent<HTMLElement>) => void;
  handleClear: () => void;
  focus?: boolean;
};

const styles = {
  searchBarContainer: () => css`
    z-index: 1000;
    border: none;

    i {
      margin-right: var(--cds-spacing-200);
    }
  `,
  searchContainer: () => css`
    flex: 1;
    margin-right: var(--cds-spacing-100);

    input {
      ${typography2.bodyPrimary};
      max-height: 36px;
      padding: var(--cds-spacing-100) var(--cds-spacing-150);

      &::placeholder {
        color: var(--cds-color-neutral-primary-weak);
        opacity: 1;
      }
    }

    button {
      border-radius: 0 4px 4px 0;
    }
    ${breakpoints.down('xs')} {
      div:first-of-type {
        min-width: 0;
      }
    }
  `,
  inputArea: () => css`
    border: 1px solid var(--cds-color-grey-600);
    box-sizing: border-box;

    /* using this to override some of the .input-area styling without impacting other pages */
    border-radius: 4px 0 0 4px !important;
    margin-right: 0 !important;
    border-right: 0 !important;

    .cif-cancel-hint {
      opacity: 1;
      margin-left: var(--cds-spacing-200);
    }
  `,
};

class SearchBar extends React.Component<Props> {
  containerRef: HTMLDivElement | null = null;

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof SearchBar.contextTypes>;

  static defaultProps = {
    handleBlur: () => {},
    handleTab: () => {},
    handleShiftTab: () => {},
    handleClear: () => {},
  };

  componentDidMount() {
    const element = document.getElementById('search-bar-expanded-input');
    if (element) {
      element.focus();
    }
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { handleChange } = this.props;
    const { value } = e.target;
    handleChange(value);
  };

  handleSubmitClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // prevent blur event
    const { handleSubmit } = this.props;
    handleSubmit();
  };

  handleClear = () => {
    const { router } = this.context;
    const {
      location: { query, pathname },
      params,
    } = router;
    const { handleBlur, handleClear } = this.props;

    if (!query.q) {
      // No query so there is no active search yet, just blur it.
      handleBlur();
    }

    handleClear();
    // Otherwise there is an active search, transition to no search at all.
    delete query.q;
    router.push({
      pathname,
      params,
      query,
    });
  };

  handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const { handleBlur } = this.props;
    if (handleBlur && e.relatedTarget) {
      // relatedTarget is the element receiveing focus
      // this checks if the focus onBlur is leaving the container, if it is trigger a blur
      if (!this.containerRef?.contains(e.relatedTarget as Node)) {
        handleBlur();
      }
    }
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { handleSubmit, handleBlur, handleShiftTab } = this.props;
    switch (e.key) {
      case 'Enter': {
        handleSubmit();
        break;
      }
      case 'Tab': {
        if (e.shiftKey && handleBlur) {
          handleBlur();
          handleShiftTab(e);
        }
        break;
      }
      default:
        break;
    }
  };

  handleClearKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { handleBlur, handleShiftTab } = this.props;
    switch (e.key) {
      case 'Enter': {
        this.handleClear();
        break;
      }
      case 'Tab': {
        if (e.shiftKey && handleBlur) {
          handleBlur();
          handleShiftTab(e);
        }
        break;
      }
      default:
        break;
    }
  };

  submitKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { handleTab, handleShiftTab, handleSubmit } = this.props;
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        handleShiftTab(e);
      } else {
        handleTab(e);
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      handleSubmit();
    }
  };

  render() {
    const { query } = this.props;
    const closeIcon = (
      <i
        role="button"
        onMouseDown={this.handleClear}
        tabIndex={0}
        onKeyDown={this.handleClearKeyDown}
        className="cif-cancel-hint close-icon"
        aria-label={_t('Clear Search')}
      />
    );

    return (
      <div
        className="rc-ForumsV2__SearchBar search-bar expand-block expanded flex-1"
        css={styles.searchBarContainer}
        ref={(ref) => (this.containerRef = ref)}
        onBlur={this.handleBlur}
      >
        <Grid container direction="row" wrap="nowrap" css={styles.searchContainer}>
          <div className="input-area" css={styles.inputArea}>
            <TrackedTextInput
              trackingName="search_bar"
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              value={query}
              onChange={this.handleChange}
              onKeyDown={this.handleKeyDown}
              placeholder={_t('Search')}
              aria-label={_t('Search Input')}
              className="search-input"
              id="search-bar-expanded-input"
            />
            {typeof query === 'string' && closeIcon}
          </div>
          <Button
            type="button"
            onMouseDown={this.handleSubmitClick}
            onKeyDown={this.submitKeyDown}
            aria-label={_t('submit search')}
          >
            {_t('Search')}
          </Button>
        </Grid>
      </div>
    );
  }
}

export default SearchBar;
